/**
 * DesktopMealCard
 *
 * Full column-width meal card for the desktop magazine grid.
 * Matches the Stitch reference: dietary badge, serif italic title,
 * "MEAL TYPE • TIME" footer, and always-visible action buttons
 * (swap, mark cooked, delete). Drag handle for reordering.
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useDispatch, useSelector } from 'react-redux';
import { PlannedMeal, MealType, markMealAsCooked, unmarkMealAsCooked } from '../../redux/mealPlanSlice';
import { isEntryMeal, entryTitle } from '../../redux/mealEntry';
import { useVerdictPrompt } from '../household/VerdictPromptProvider';
import { RootState } from '../../redux/store';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius, PaprikaShadows } from '../../styles/theme';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useWebDrag } from '../../hooks/useWebDragDrop';
import { useTranslation } from 'react-i18next';
import { SourceRatingBadge } from '../shared/SourceRatingBadge';
import { CoverageCircle } from './CoverageCircle';
import { CoverageInfo } from './CoverageInfo';
import { getProteinType, ProteinType } from '../../utils/proteinType';
import { CookedStamp } from './CookedStamp';
import { ApprovalThumbs } from './ApprovalThumbs';
import { CookRow } from './CookRow';
import { RemoteImage } from '../shared/RemoteImage';
import { todayIso } from '../../utils/localDate';

const isPastDate = (scheduledFor: string, today: string): boolean => scheduledFor < today;

const PROTEIN_LABEL_KEYS: Partial<Record<ProteinType, string>> = {
  fish: 'thisWeek.proteinFish',
  veggie: 'thisWeek.proteinPlantBased',
};

interface DesktopMealCardProps {
  meal: PlannedMeal;
  onPress: () => void;
  onDelete: () => void;
  onSuggestAnother: () => void;
  onMoveMeal?: (mealId: string, newDate: string, newMealType: MealType) => void;
  onMarkDone?: (meal: PlannedMeal) => void;
}

export const DesktopMealCard: React.FC<DesktopMealCardProps> = ({
  meal,
  onPress,
  onDelete,
  onSuggestAnother,
  onMoveMeal,
  onMarkDone,
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const { cardRef, isActive: isWebDragActive } = useWebDrag({ meal });

  const isFreeText = meal.recipeId === 'free-text';
  const isEntry = isEntryMeal(meal);
  const isCooked = meal.status === 'cooked';
  const proteinType = getProteinType(meal);
  const proteinKey = PROTEIN_LABEL_KEYS[proteinType];
  const proteinLabel = proteinKey ? t(proteinKey).toUpperCase() : null;
  // An entry used to read « Entry »: this layout never learnt what the planner calls them.
  const title = isFreeText
    ? (meal.freeText || t('entry.note'))
    : isEntry
      ? entryTitle(meal.entry!, () => undefined, t)
      : (meal.recipe?.title || t('thisWeek.unknownRecipe'));
  const mealTypeLabel = t(`thisWeek.mealTypes.${meal.mealType}`).toUpperCase();
  const cookTime = Number(meal.recipe?.readyInMinutes) > 0 ? `${meal.recipe!.readyInMinutes} MIN` : null;

  const dispatch = useDispatch();
  const { openVerdict } = useVerdictPrompt();
  const isSkipped = !isCooked && !isFreeText && !isEntry && isPastDate(meal.scheduledFor, todayIso());

  const handleImagePress = (e?: any) => {
    e?.stopPropagation?.();
    if (isFreeText || isEntry) return;
    if (isCooked) {
      dispatch(unmarkMealAsCooked({ mealId: meal.id }));
    } else if (meal.scheduledFor <= todayIso()) {
      dispatch(markMealAsCooked({ mealId: meal.id }));
    }
  };

  // The star tap no longer writes a number: the rating is derived from what
  // each person at the table said, so tapping opens the sheet that asks them.
  const handleRate = () => {
    if (!meal.recipeId || isFreeText || isEntry) return;
    openVerdict({ recipeId: meal.recipeId, recipeTitle: title, surface: 'cooked_stamp' });
  };

  const handlePress = () => {
    if (isFreeText) return;
    onPress();
  };

  return (
    <Pressable
      ref={cardRef}
      onPress={handlePress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={[
        styles.card,
        isHovered && styles.cardHovered,
        isSkipped && styles.cardSkipped,
      ]}
    >
      {/* Image */}
      <Pressable
        onPress={handleImagePress}
        style={styles.imageWrap}
        accessibilityRole="button"
        accessibilityLabel={isCooked ? t('thisWeek.unmarkCooked') : t('thisWeek.markCooked')}
      >
        <RemoteImage
          uri={isFreeText ? undefined : meal.recipe?.image}
          variant="thumb"
          style={styles.image}
          fallback={
            <View style={styles.fallbackContainer}>
              <MaterialCommunityIcons
                name="silverware-fork-knife"
                size={24}
                color={Paprika.textMuted}
                style={styles.fallbackIcon}
              />
            </View>
          }
        />
        {isCooked && (
          <CookedStamp
            recipeId={String(meal.recipeId)}
            shape="rect"
            size="desktop"
            onRate={handleRate}
          />
        )}
        {isSkipped && (
          <View style={styles.skipPill}>
            <Text style={styles.skipPillText}>{t('thisWeek.notCooked')}</Text>
          </View>
        )}
      </Pressable>

      {/* Content */}
      <View style={styles.content}>
        {/* Title row: title + dietary badge + coverage circle */}
        <View style={styles.titleRow}>
          <Text style={styles.title} numberOfLines={2}>{title}</Text>
          {!!proteinLabel && (
            <View style={styles.proteinBadge}>
              <Text style={styles.proteinBadgeText}>{proteinLabel}</Text>
            </View>
          )}
          {meal.matchMetadata?.ingredientCoverage && (
            <CoverageCircle coveragePercent={meal.matchMetadata.ingredientCoverage.coveragePercent} size={40} />
          )}
        </View>

        {/* Meta line: MEAL TYPE · TIME */}
        <View style={styles.metaRow}>
          <Text style={styles.metaText}>
            {mealTypeLabel}{cookTime ? ` · ${cookTime}` : ''}
          </Text>
        </View>

        {/* Source rating badge */}
        {!isFreeText && !!meal.recipe?.sourceUrl && (
          <View style={styles.sourceRow}>
            <SourceRatingBadge
              sourceUrl={meal.recipe.sourceUrl}
              sourceRating={meal.recipe.sourceRating}
              sourceRatingCount={meal.recipe.sourceRatingCount}
              size="sm"
            />
          </View>
        )}

        {/* Cook assignment */}
        <CookRow mealId={meal.id} variant="compact" isHovered={isHovered} />

        {/* Ingredient coverage */}
        {meal.matchMetadata?.ingredientCoverage && (
          <CoverageInfo coverage={meal.matchMetadata.ingredientCoverage} />
        )}

        {/* Action buttons */}
        <View style={styles.actionButtons}>
          {!isFreeText && (
            <Pressable
              style={styles.actionButton}
              onPress={(e: any) => { e?.stopPropagation?.(); onSuggestAnother(); }}
              testID="meal-suggest-another"
            >
              <Feather name="refresh-cw" size={16} color={Paprika.textSecondary} />
            </Pressable>
          )}
          {onMarkDone && meal.scheduledFor <= todayIso() && (
            <Pressable
              style={styles.actionButton}
              onPress={(e: any) => { e?.stopPropagation?.(); onMarkDone(meal); }}
            >
              <Feather
                name="check-circle"
                size={14}
                color={isCooked ? '#27AE60' : Paprika.textMuted}
              />
            </Pressable>
          )}
          <Pressable
            style={styles.actionButton}
            onPress={(e: any) => { e?.stopPropagation?.(); onDelete(); }}
          >
            <Feather name="trash-2" size={14} color={Paprika.textMuted} />
          </Pressable>
          <ApprovalThumbs mealId={meal.id} variant="outline" />
        </View>
      </View>

      {/* Drag handle — always visible as affordance, highlighted on hover */}
      {isWebDragActive && (
        <View style={[styles.dragHandle, isHovered && styles.dragHandleVisible]}>
          <Feather name="menu" size={16} color={Paprika.textMuted} />
        </View>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.md,
    overflow: 'hidden',
    marginBottom: PaprikaSpacing.sm,
    ...PaprikaShadows.card,
  },
  cardHovered: {
    ...PaprikaShadows.cardHover,
    transform: [{ scale: 1.01 }],
  },
  cardSkipped: {
    opacity: 0.55,
  },
  imageWrap: {
    position: 'relative',
  },
  image: {
    width: '100%',
    aspectRatio: 16 / 10,
  },
  skipPill: {
    position: 'absolute',
    bottom: 6,
    left: 6,
    backgroundColor: 'rgba(255,255,255,0.9)',
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 3,
  },
  skipPillText: {
    fontSize: 9,
    letterSpacing: 1.2,
    color: Paprika.textMuted,
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  fallbackContainer: {
    width: '100%',
    aspectRatio: 16 / 10,
    backgroundColor: Paprika.surfaceLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fallbackIcon: {
    opacity: 0.4,
  },
  content: {
    padding: PaprikaSpacing.sm,
    paddingHorizontal: 10,
  },

  // Title row with optional dietary badge
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: PaprikaSpacing.xs,
    marginBottom: 4,
  },
  title: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
    flex: 1,
  },
  proteinBadge: {
    backgroundColor: Paprika.surfaceLight,
    borderRadius: PaprikaRadius.xl,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginTop: 2,
  },
  proteinBadgeText: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textSecondary,
    fontSize: 9,
    letterSpacing: 0.6,
  },

  // Meta line
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 4,
    gap: 4,
  },
  metaText: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
    letterSpacing: 0.6,
    fontSize: 10,
  },
  sourceRow: {
    marginBottom: 4,
  },

  // Action buttons
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0,
  },
  actionButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Drag handle
  dragHandle: {
    position: 'absolute',
    top: PaprikaSpacing.xs,
    right: PaprikaSpacing.xs,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.3,
  },
  dragHandleVisible: {
    opacity: 1,
  },
});

export default DesktopMealCard;
